'use client';

import { Play, Video, FileText, Film, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { cn } from '@/lib/utils';

interface MomentCardProps {
  playbackId: string;
  startTime: number;
  endTime: number;
  title?: string;
  thumbnailUrl?: string;
  videoType?: 'moment' | 'full' | 'memo' | 'preview';
  postTitle?: string;
  onWatch: () => void;
}

const VIDEO_TYPE_CONFIG = {
  moment: {
    label: 'Moment',
    icon: Film,
    className: 'bg-emerald-500/90 text-white',
  },
  full: {
    label: 'Full Video',
    icon: Video,
    className: 'bg-blue-500/90 text-white',
  },
  memo: {
    label: 'Memo',
    icon: FileText,
    className: 'bg-apprendo-500/90 text-white',
  },
  preview: {
    label: 'Preview',
    icon: Eye,
    className: 'bg-amber-500/90 text-white',
  },
};

export function MomentCard({
  playbackId,
  startTime,
  endTime,
  title,
  thumbnailUrl,
  videoType = 'moment',
  postTitle,
  onWatch,
}: MomentCardProps) {
  const duration = endTime - startTime;
  const isFullVideo = videoType === 'full';
  const thumbnailSrc =
    thumbnailUrl ||
    `https://image.mux.com/${playbackId}/thumbnail.jpg?time=${isFullVideo ? 0 : startTime}`;

  const typeConfig = VIDEO_TYPE_CONFIG[videoType] || VIDEO_TYPE_CONFIG.moment;
  const TypeIcon = typeConfig.icon;
  const displayTitle = postTitle || title;

  return (
    <div className="flex gap-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors group">
      {/* Thumbnail */}
      <div className="relative w-32 h-20 flex-shrink-0 bg-black rounded overflow-hidden">
        <Image
          src={thumbnailSrc}
          alt={displayTitle || 'Video'}
          fill
          className="object-cover"
          unoptimized
        />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Play className="h-8 w-8 text-white" />
        </div>

        {/* Video Type Badge (top-left) - only show for non-moment types */}
        {typeConfig.label && TypeIcon && (
          <div className={cn(
            "absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-1",
            typeConfig.className
          )}>
            <TypeIcon className="h-3 w-3" />
            {typeConfig.label}
          </div>
        )}

        {/* Duration Badge (bottom-right) - show different info based on type */}
        <div className="absolute bottom-1 right-1 bg-black/80 px-1.5 py-0.5 rounded text-xs text-white">
          {isFullVideo ? (
            duration > 0 ? formatDuration(duration) : 'Full'
          ) : (
            formatDuration(duration)
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          {displayTitle && (
            <p className="text-sm font-medium line-clamp-2 mb-1">{displayTitle}</p>
          )}
          {/* Show time range for moments, hide for full videos */}
          {!isFullVideo && startTime > 0 && (
            <p className="text-xs text-muted-foreground">
              {formatTime(startTime)} - {formatTime(endTime)}
            </p>
          )}
          {isFullVideo && (
            <p className="text-xs text-muted-foreground">
              Click to preview in panel
            </p>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="w-fit mt-2"
          onClick={onWatch}
        >
          <Play className="h-3 w-3 mr-1.5" />
          {isFullVideo ? 'Preview' : 'Watch'}
        </Button>
      </div>
    </div>
  );
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins > 0) {
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  return `${secs}s`;
}
